import { Suspense, lazy, useEffect, useState } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Route,
  Navigate,
} from "react-router-dom";
import { useAuthStore, startGuestSession } from "@/stores/authStore";
import LoginPage from "@/pages/LoginPage";
import DecksPage from "@/pages/DecksPage";
import DeckBuilderPage from "@/pages/DeckBuilderPage";
import AuthCallbackPage from "@/pages/AuthCallbackPage";
import { loadCards } from "@/lib/cardsLoader";
// 初回表示に不要な重いページは別チャンクにして、最初のバンドルを軽くする
const LobbyPage = lazy(() => import("@/pages/LobbyPage"));
const PracticePage = lazy(() => import("@/pages/PracticePage"));
const GamePage = lazy(() => import("@/pages/GamePage"));
const StyleGuidePage = lazy(() => import("@/pages/StyleGuidePage"));
import { Toaster } from "@/components/ui/sonner";
import { CardsGate } from "@/components/CardsGate";

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const loggedOut = useAuthStore((s) => s.loggedOut);
  const [guestFailed, setGuestFailed] = useState(false);
  // 未ログインならゲストとして自動で入る（Googleログインは任意）
  useEffect(() => {
    if (user || loggedOut) return;
    startGuestSession().then((ok) => setGuestFailed(!ok));
  }, [user, loggedOut]);
  if (!user) {
    if (guestFailed || loggedOut) return <Navigate to="/login" replace />;
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <span className="size-5 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-primary" />
      </div>
    );
  }
  return (
    <CardsGate>
      <Suspense
        fallback={
          <div className="flex min-h-screen items-center justify-center bg-background">
            <span className="size-5 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-primary" />
          </div>
        }
      >
        {children}
      </Suspense>
    </CardsGate>
  );
}

// データルーター（createBrowserRouter）を使う。
// useBlocker（デッキ編集中の離脱確認）はデータルーターでのみ動作するため。
const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/styleguide" element={<StyleGuidePage />} />
      {/* ホーム = デッキリスト */}
      <Route
        path="/"
        element={
          <PrivateRoute>
            <DecksPage />
          </PrivateRoute>
        }
      />
      {/* 旧パス。ブックマーク救済のためホームへ寄せる */}
      <Route path="/decks" element={<Navigate to="/" replace />} />
      <Route
        path="/lobby"
        element={
          <PrivateRoute>
            <LobbyPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/practice"
        element={
          <PrivateRoute>
            <PracticePage />
          </PrivateRoute>
        }
      />
      {/* 新規作成(/deck)と編集(/deck/:deckId)は同じルート。
          インポート時に /deck へ戻しても再マウントされず編集中の状態を保てる */}
      <Route
        path="/deck/:deckId?"
        element={
          <PrivateRoute>
            <DeckBuilderPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/game/:roomId"
        element={
          <PrivateRoute>
            <GamePage />
          </PrivateRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </>
  )
);

export default function App() {
  const loggedIn = useAuthStore((s) => !!s.user);
  // ログインしたら、画面遷移を待たずにカードマスタの取得を始める（失敗時は CardsGate が再試行UIを出す）
  useEffect(() => {
    if (loggedIn) loadCards().catch(() => {});
  }, [loggedIn]);

  return (
    <>
      <RouterProvider router={router} />
      <Toaster />
    </>
  );
}
